import { toHex } from '@pactswap/cwap-cm-lib/shared';
import { bech32, bech32m } from 'bech32';
import bs58 from 'bs58';
import { BtcAddressType, BtcLikeNetworkParams, OrderBatchFields } from './constants';
const getBech32Type = (address) => {
    const bech32Prefixes = Object.values(BtcLikeNetworkParams)
        .map((network) => network.bech32)
        .filter(Boolean);
    let decoded;
    if (bech32Prefixes.some((prefix) => address.startsWith(`${prefix}1p`))) {
        decoded = bech32m.decode(address);
    }
    else if (bech32Prefixes.some((prefix) => address.startsWith(`${prefix}1`))) {
        decoded = bech32.decode(address);
    }
    else {
        throw new Error('Invalid address prefix');
    }
    const witnessVersion = decoded.words[0];
    const data = bech32.fromWords(decoded.words.slice(1));
    let type;
    if (data.length === 20) {
        type = BtcAddressType.p2wpkh;
    }
    else if (witnessVersion === 1) {
        type = BtcAddressType.p2tr;
    }
    else {
        type = BtcAddressType.p2wsh;
    }
    return type;
};
export const getBtcAddressType = (address, network) => {
    const bech32Prefixes = network
        ? [BtcLikeNetworkParams[network].bech32]
        : Object.values(BtcLikeNetworkParams)
            .map((network) => network.bech32)
            .filter(Boolean);
    try {
        const addressPrefix = bech32Prefixes.find((prefix) => address.toLowerCase().startsWith(prefix));
        if (addressPrefix && bech32Prefixes.some((prefix) => prefix === addressPrefix)) {
            return getBech32Type(address);
        }
        const decoded = bs58.decode(address);
        const version = decoded[0];
        const scriptHashPrefixes = network
            ? [BtcLikeNetworkParams[network].scriptHash]
            : Object.values(BtcLikeNetworkParams).map((network) => network.scriptHash);
        if (scriptHashPrefixes.includes(version)) {
            return BtcAddressType.p2sh;
        }
        const publicKeyHashPrefixes = network
            ? [BtcLikeNetworkParams[network].publicKeyHash]
            : Object.values(BtcLikeNetworkParams).map((network) => network.publicKeyHash);
        if (publicKeyHashPrefixes.includes(version)) {
            return BtcAddressType.p2pkh;
        }
        throw new Error();
    }
    catch {
        throw new Error(`Invalid address ${address}`);
    }
};
export const serializeOrdersBatch = (ordersBatch) => {
    return ordersBatch.map(([count, base, tunes]) => [
        count,
        [
            base.baseAmount ? toHex(base.baseAmount) : null,
            base.l1Amount ? toHex(base.l1Amount) : null,
            base.baseRecipient ? base.baseRecipient : null,
            base.minL1Amount ? toHex(base.minL1Amount) : null,
        ],
        tunes?.map((tune) => Object.fromEntries(Object.entries(tune).map(([key, value]) => [
            OrderBatchFields.findIndex((field) => field === key),
            ['baseAmount', 'l1Amount', 'minL1Amount'].includes(key) ? toHex(value) : value,
        ]))),
    ]);
};
export const deserializeBatchToList = (ordersBatch) => {
    return ordersBatch
        .map((orderSet) => {
        const orders = Array(orderSet[0])
            .fill(Object.fromEntries(OrderBatchFields.map((field, i) => [
            field,
            orderSet[1][i],
        ]).filter(([_, value]) => value !== null)))
            .map((order, i) => ({
            ...order,
            ...(orderSet[2]?.[i] &&
                Object.fromEntries(Object.entries(orderSet[2][i]).map(([key, value]) => [OrderBatchFields[Number(key)], value]))),
        }))
            .map((order) => Object.fromEntries(Object.entries(order).map(([key, value]) => {
            return [
                key,
                ['baseAmount', 'l1Amount', 'minL1Amount'].includes(key) && value ? BigInt(value) : value,
            ];
        })));
        return orders;
    }, [])
        .flat();
};
export const ordersBatchToList = (ordersBatch) => {
    return ordersBatch
        .map((orderSet) => {
        const orders = Array(orderSet[0])
            .fill(orderSet[1])
            .map((order, i) => ({ ...order, ...orderSet[2]?.[i] }));
        return orders;
    }, [])
        .flat();
};
